import React, { FunctionComponent, useMemo } from 'react'
import { useSelector } from 'react-redux'
import Flex from 'typescript-styled-flex'
import { useTranslation } from 'react-i18next'
import useFormatDate from '../../../../hooks/useFormatDate'
import { EventStatus } from '../../../../models/Event'
import { eventStatus } from '../../../../reducers/bookingCalendar/selectors/events'
import { theme } from '../../../../theme'
import { IEventSearchResult } from '../../../../types/Search/EventSearch'
import Color from '../../../ColorPalette/Color'
import { IWithSize } from '../../../../containers/BookingCalendar/Calendar/Calendar'
import {
  EventSearchResultContainer,
  EventSearchResultDetails,
  EventSearchResultDetailsContainer,
  EventSearchResultName,
} from './styled'

const ResultComponent: FunctionComponent<
  IResultCompontentProps & IWithSize
> = ({
  name,
  color,
  startDate,
  finishDate,
  id,
  email,
  phone,
  bookingId = '',
  isBooking = false,
  isICalendar = false,
  cancelled = false,
  onClick,
  size = 1,
}) => {
  const [t] = useTranslation()
  const formatDate = useFormatDate()
  const onEventClick = () => {
    onClick(id)
  }

  return (
    <EventSearchResultContainer
      size={size}
      onClick={onEventClick}
      isCancelled={cancelled}
      isBooking={isBooking}
      isICalendar={isICalendar}
    >
      <Flex row={true} alignCenter={true} full={true}>
        <Flex style={{ minWidth: `${30 * size}px` }}>
          <Color
            size={size}
            color={color}
            width={`${14 * size}px`}
            height={`${14 * size}px`}
            style={{ margin: 0 }}
          />
        </Flex>
        <Flex full={true}>
          <EventSearchResultName size={size}>{name}</EventSearchResultName>
        </Flex>
        {(isBooking || isICalendar) && (
          <Flex justifyEnd={true} style={{ minWidth: `${30 * size}px` }}>
            <Color
              size={size}
              color={
                isBooking
                  ? theme.colors.palette.booking
                  : theme.colors.palette.icalendar
              }
              width={`${14 * size}px`}
              height={`${14 * size}px`}
              style={{ margin: 0 }}
            />
          </Flex>
        )}
      </Flex>
      <Flex
        style={{ marginTop: `${10 * size}px` }}
        row={true}
        alignStart={true}
        wrap={true}
      >
        <Flex style={{ flexBasis: '50%' }}>
          <Flex
            style={{
              minWidth: `${30 * size}px`,
              paddingRight: `${10 * size}px`,
            }}
          >
            <EventSearchResultDetails size={size} label={true}>
              {t('reservation_from')}
            </EventSearchResultDetails>
          </Flex>
          <Flex>
            <EventSearchResultDetails size={size}>
              {formatDate(startDate)}
            </EventSearchResultDetails>
          </Flex>
        </Flex>
        <Flex style={{ flexBasis: '50%' }}>
          <Flex style={{ paddingRight: `${10 * size}px` }}>
            <EventSearchResultDetails size={size} label={true}>
              {t('reservation_to')}
            </EventSearchResultDetails>
          </Flex>
          <Flex>
            <EventSearchResultDetails size={size}>
              {formatDate(finishDate)}
            </EventSearchResultDetails>
          </Flex>
        </Flex>
      </Flex>

      {phone && (
        <Flex
          row={true}
          alignStart={true}
          style={{ marginTop: `${10 * size}px` }}
        >
          <Flex
            style={{
              minWidth: `${30 * size}px`,
              paddingRight: `${10 * size}px`,
            }}
          >
            <EventSearchResultDetails size={size} label={true}>
              {t('reservation_phone')}
            </EventSearchResultDetails>
          </Flex>
          <EventSearchResultDetailsContainer>
            <EventSearchResultDetails size={size}>
              {phone}
            </EventSearchResultDetails>
          </EventSearchResultDetailsContainer>
        </Flex>
      )}

      {email && (
        <Flex
          row={true}
          alignStart={true}
          style={{ marginTop: `${10 * size}px` }}
        >
          <Flex
            style={{
              minWidth: `${30 * size}px`,
              paddingRight: `${10 * size}px`,
            }}
          >
            <EventSearchResultDetails size={size} label={true}>
              {t('email')}
            </EventSearchResultDetails>
          </Flex>
          <EventSearchResultDetailsContainer>
            <EventSearchResultDetails size={size}>
              {email}
            </EventSearchResultDetails>
          </EventSearchResultDetailsContainer>
        </Flex>
      )}

      <Flex
        row={true}
        alignStart={true}
        style={{ marginTop: `${10 * size}px` }}
      >
        <Flex
          style={{ minWidth: `${30 * size}px`, paddingRight: `${10 * size}px` }}
        >
          <EventSearchResultDetails size={size} label={true}>
            ID
          </EventSearchResultDetails>
        </Flex>
        <Flex>
          <EventSearchResultDetails size={size}>{id}</EventSearchResultDetails>
        </Flex>
      </Flex>
      {isBooking && (
        <Flex
          row={true}
          alignStart={true}
          style={{ marginTop: `${10 * size}px` }}
        >
          <Flex style={{ paddingRight: `${10 * size}px` }}>
            <EventSearchResultDetails size={size} label={true}>
              Booking ID
            </EventSearchResultDetails>
          </Flex>
          <Flex>
            <EventSearchResultDetails size={size}>
              {bookingId}
            </EventSearchResultDetails>
          </Flex>
        </Flex>
      )}
      {cancelled && (
        <div style={{ marginTop: `${10 * size}px` }}>
          <EventSearchResultDetails size={size} label={true}>
            {t('reservation_stage_5_cancelled')}
          </EventSearchResultDetails>
        </div>
      )}
    </EventSearchResultContainer>
  )
}

const EventSearchResult: FunctionComponent<IEventSearchResultProps & IWithSize> = ({ event, onClick, size = 1 }) => {
  const fStatus = useSelector(eventStatus)
  const status = useMemo(() => fStatus(event), [event, fStatus])

  return (
    <ResultComponent
      size={size}
      name={event.guest.name || ''}
      color={status.color}
      startDate={event.startDate}
      finishDate={event.finishDate}
      id={event.id}
      email={event.guest.email}
      phone={event.guest.phone}
      bookingId={event.bookingId}
      isBooking={event.bookingId && event.bookingId !== '0'}
      isICalendar={!!event.iCalendarId}
      cancelled={event.stage === EventStatus.Canceled}
      onClick={onClick}
    />
  )
}

export interface IResultCompontentProps {
  name: string
  color: string
  startDate: Date
  finishDate: Date
  id: string
  email?: string
  phone?: string
  bookingId?: string
  isBooking?: boolean
  isICalendar?: boolean
  cancelled?: boolean
  onClick?: IEventSearchResultProps['onClick']
}

export interface IEventSearchResultProps {
  event: IEventSearchResult
  onClick?: (id: IEventSearchResult['id']) => void
}

export { ResultComponent, EventSearchResult }
export default EventSearchResult
